import { sessionRetentionUnits } from './session-retention.js';
import { normalizeActivityFrame, normalizeResolution } from './normalized-activity.js';
import { redactInteractionResponse } from '@borgee/agent-provider-sdk';
import { validSessionAvailability, validTimelineReplacement, timelineReplacementDigest } from '@borgee/plugin-sdk/bpp-normalized';
import { isPublicAgentTimelineEvent, PROTOCOL_VERSION } from '@borgee/agent-remote-protocol';
import { SessionCapacityError, sessionRecordBytes, sessionKey, isDirectMessageSessionScope } from './journal.js';
import { SessionProjector, restoredInteractionFrames } from './session-projection.js';
export { sessionKey } from './journal.js';
const recoveryOf = (model) => model.authority.status === 'recovering' ? model.authority.recovery : undefined;
const frozenOf = (model) => 'frozen' in model.authority ? model.authority.frozen : undefined;
const authorityView = (model) => model.authority.status === 'recovering'
    ? { status: 'recovering', stage: model.authority.recovery.stage, operationId: model.authority.recovery.operationId, nativeSessionId: model.authority.recovery.nativeSessionId }
    : { status: model.authority.status };
export function createSessionConsumer(options) {
    const journal = options.journal;
    const notify = (observer, callback, scope) => {
        try {
            callback();
        }
        catch (cause) {
            try {
                options.onObserverError?.({ observer, cause, ...(scope ? { scope: { ...scope } } : {}) });
            }
            catch { /* Diagnostic observers cannot change committed state or prevent other notifications. */ }
        }
    };
    const notifyCommit = (evidence) => notify('commit', () => options.onCommit?.(structuredClone(evidence)), evidence.scope);
    const listeners = new Set();
    let state;
    const get = () => state;
    const set = (update) => {
        const previous = state;
        state = { ...state, ...typeof update === 'function' ? update(state) : update };
        for (const listener of listeners)
            notify('subscription', () => listener(state, previous));
    };
    const initial = (() => {
        const models = new Map();
        const revoked = new Set();
        const sequences = new Map();
        const producerKey = sessionKey;
        const channelKey = (scope) => JSON.stringify([scope.accountId, scope.guildId, scope.channelId]);
        let timer;
        const alive = (model) => models.get(sessionKey(model.scope)) === model;
        const discardIngress = (model) => {
            for (const item of model.deferredFrames.splice(0))
                if (item.kind === 'replacement')
                    item.resolve(false);
            model.deferredBytes = 0;
            for (const sync of model.syncs.values()) {
                clearTimeout(sync.timer);
                sync.resolve(false);
            }
            model.syncs.clear();
            model.candidates.clear();
        };
        const finishSync = (model, id, success) => {
            const op = model.syncs.get(id);
            if (!op)
                return;
            clearTimeout(op.timer);
            model.syncs.delete(id);
            op.resolve(success);
            if (!success && model.authority.status === 'recovering' && model.authority.recovery.operationId === id) {
                model.authority = { status: 'paused', frozen: model.authority.frozen };
                model.timelineRecoveryStatus = 'failed';
                publish(model);
            }
            const authority = model.replacementAuthority;
            const authorizes = (sync) => authority && sync.generation === authority.generation && sync.receipt?.timeline_epoch === authority.epoch && sync.receipt.replacement_id === authority.replacementId;
            if (!success && authorizes(op) && ![...model.syncs.values()].some(authorizes))
                model.replacementAbort?.abort();
            if (!model.syncs.size)
                model.candidates.clear();
        };
        const matchingSyncs = (model, frame) => [...model.syncs].filter(([, op]) => op.generation === model.generation && op.receipt?.timeline_epoch === frame.event.epoch && op.receipt.replacement_id === frame.event.replacement_id);
        const failTimeline = (model) => { for (const id of model.syncs.keys())
            finishSync(model, id, false); model.candidates.clear(); model.timelineRecoveryStatus = 'failed'; };
        const drainIngress = (model) => {
            while (alive(model) && !model.loading && !model.replacing && model.deferredFrames.length) {
                const item = model.deferredFrames.shift();
                model.deferredBytes -= item.bytes;
                if (item.kind === 'frame')
                    get().receive(model.scope, item.frame, item.receivedAt, item.presentation);
                else {
                    void get().replaceTimeline(model.scope, item.frame).then(item.resolve);
                    return;
                }
            }
        };
        const enqueueIngress = (model, item) => {
            if (model.deferredBytes + item.bytes > 4 * 1024 * 1024 || model.deferredFrames.length >= 4096) {
                pause(model, { kind: 'omission', cause: new SessionCapacityError() });
                model.timelineRecoveryStatus = 'failed';
                return false;
            }
            model.deferredFrames.push(item);
            model.deferredBytes += item.bytes;
            return true;
        };
        const publish = (model) => {
            if (!alive(model))
                return;
            const related = [...models.values()].filter(value => channelKey(value.scope) === channelKey(model.scope) && value.scope.agentId === model.scope.agentId);
            const tasks = {};
            const seen = new Set();
            for (const source of related)
                for (const [taskId, task] of Object.entries((frozenOf(source)?.projector ?? source.projector).taskSnapshot(frozenOf(source)?.projector ? frozenOf(source)?.generation : source.generation))) {
                    tasks[taskId] = { ...task, ...((source.authority.status !== 'ready') ? { nativeCapabilitiesLive: false } : {}), sessionScope: !seen.has(taskId) && source.scope.bindingId ? source.scope : undefined };
                    seen.add(taskId);
                }
            notify('presentation', () => options.onPresentation?.(model.scope, tasks, model.projector.view().capacityNotice, (taskId, resolution, token) => {
                const owner = related.find(value => value.projector.view(value.generation).pending.some(pending => pending.token === token && pending.taskId === taskId));
                if (owner)
                    get().applyResolution(owner.scope, taskId, resolution, token);
            }), model.scope);
            set((state) => ({ sessions: { ...state.sessions, [sessionKey(model.scope)]: {
                        authority: authorityView(model), observationGeneration: model.observationGeneration, observationAvailability: model.availability && { ...model.availability },
                        ...(frozenOf(model)?.projector ?? model.projector).view(frozenOf(model)?.projector ? frozenOf(model)?.generation : model.generation), ...((model.authority.status !== 'ready') ? { servicePaused: true, nativeCapabilitiesLive: false } : {}), synchronizing: !!recoveryOf(model), recoveryStatus: model.recoveryStatus, timelineRecoveryStatus: model.timelineRecoveryStatus, loading: model.loading, loaded: model.loaded, storageError: model.readError || model.omissionError || model.writeError ? [model.readError, model.omissionError, model.writeError].filter((error) => !!error) : undefined, trimmed: model.trimmed,
                    } } }));
        };
        const pause = (model, error, shared = false) => {
            for (const affected of shared ? models.values() : [model]) {
                affected.authority = { status: 'paused', frozen: frozenOf(affected) ?? { generation: affected.generation, projector: affected.checkpoint ?? new SessionProjector(true) } };
                affected.replacementAbort?.abort();
                discardIngress(affected);
                if (error.kind === 'read')
                    affected.readError = error;
                else
                    affected.writeError = error;
                publish(affected);
            }
        };
        const commitRecovery = (model) => {
            if (model.authority.status !== 'recovering')
                throw new Error('Session recovery is not active.');
            model.checkpoint = model.projector.fork();
            model.authority = { status: 'ready' };
            model.readError = undefined;
            model.omissionError = undefined;
        };
        const forgetRecords = (model, ids) => {
            for (const id of ids)
                model.removed.add(id);
            model.records = model.records.filter(record => !ids.has(record.id));
            model.queued = model.queued.filter(record => !ids.has(record.id));
            model.bytes = model.records.reduce((sum, record) => sum + sessionRecordBytes(record), 0) + model.cuts.reduce((sum, cut) => sum + new TextEncoder().encode(JSON.stringify(cut)).byteLength, 0);
            model.metadataDirty = true;
        };
        const admit = (model, record) => {
            if (record.frame.task_id && !record.presentation?.taskUnavailable && !model.projector.taskIds().includes(record.frame.task_id)) {
                const locations = [...models.values()].filter(value => channelKey(value.scope) === channelKey(model.scope))
                    .flatMap(value => value.projector.taskIds().map(taskId => ({ model: value, taskId })));
                if (locations.length >= 16) {
                    const victim = locations.find(value => !value.model.projector.hasRequests(value.taskId) && !value.model.cuts.some(cut => cut.records.some(row => row.frame.task_id === value.taskId)));
                    if (!victim)
                        return model.projector.rejectTask(record);
                    victim.model.projector.removeTask(victim.taskId);
                    forgetRecords(victim.model, new Set(victim.model.records.filter(value => value.frame.task_id === victim.taskId).map(value => value.id)));
                    victim.model.trimmed = true;
                    publish(victim.model);
                }
            }
            return model.projector.append(record);
        };
        const rebuild = (model) => {
            model.projector = new SessionProjector(true);
            model.ordinal = 0;
            const retained = [];
            const ordered = [...model.records.map(record => ({ ordinal: record.ordinal, record })), ...model.cuts.map(cut => ({ ordinal: cut.ordinal, cut }))].sort((left, right) => left.ordinal - right.ordinal);
            for (const value of ordered) {
                if ('cut' in value) {
                    try {
                        model.projector.replaceTimeline(value.cut);
                    }
                    catch (cause) {
                        model.readError = { kind: 'read', cause };
                    }
                    model.ordinal = Math.max(model.ordinal, value.cut.ordinal, ...value.cut.records.map(record => record.ordinal));
                    continue;
                }
                const record = { ...value.record };
                model.ordinal = Math.max(model.ordinal, record.ordinal);
                retained.push(value.record);
                const frame = normalizeActivityFrame(record.frame);
                if (!frame) {
                    model.readError = { kind: 'read', cause: new Error('Stored Session activity is unreadable.') };
                    continue;
                }
                record.frame = frame;
                const event = record.resolution ? normalizeResolution(record.resolution) : frame.event;
                if (!event) {
                    model.readError = { kind: 'read', cause: new Error('Stored Session resolution is unreadable.') };
                    continue;
                }
                model.ordinal = Math.max(model.ordinal, record.ordinal);
                try {
                    admit(model, record);
                }
                catch (cause) {
                    pause(model, { kind: 'read', cause });
                    break;
                }
            }
            model.records = retained;
            model.bytes = model.records.reduce((sum, record) => sum + sessionRecordBytes(record), 0) + model.cuts.reduce((sum, cut) => sum + new TextEncoder().encode(JSON.stringify(cut)).byteLength, 0);
        };
        const trimMemory = (model) => {
            if (model.bytes <= journal.maxBytes)
                return;
            const removed = new Set();
            for (const unit of sessionRetentionUnits(model.records)) {
                if (model.bytes <= journal.maxBytes)
                    break;
                if (unit.protected)
                    continue;
                for (const record of unit.records) {
                    removed.add(record.id);
                    model.bytes -= sessionRecordBytes(record);
                }
            }
            for (const id of removed)
                model.removed.add(id);
            model.metadataDirty = true;
            model.records = model.records.filter((record) => !removed.has(record.id));
            model.queued = model.queued.filter((record) => !removed.has(record.id));
            model.trimmed = true;
            rebuild(model);
        };
        const save = async (model) => {
            await model.load;
            if (frozenOf(model)?.projector && !recoveryOf(model) || recoveryOf(model)?.stage === 'preparing')
                return;
            if (model.saving) {
                await model.saving;
                if (model.queued.length || model.metadataDirty)
                    return save(model);
                return;
            }
            if (!model.queued.length && !model.metadataDirty)
                return;
            const checkpoint = model.projector.fork();
            const batch = model.queued.splice(0);
            model.savingIds = new Set(batch.map((record) => record.id));
            const removed = [...model.removed];
            model.removed.clear();
            model.metadataDirty = false;
            let committed;
            model.saving = (async () => {
                try {
                    const result = await journal.append(model.scope, batch, { trimmed: model.trimmed, removeIds: removed });
                    if (model.authority.status === 'ready')
                        model.writeError = undefined;
                    if (model.authority.status === 'ready')
                        model.checkpoint = checkpoint;
                    model.trimmed ||= result.trimmed;
                    if (result.removedIds.length) {
                        const removed = new Set(result.removedIds);
                        model.records = model.records.filter((record) => !removed.has(record.id));
                        model.records.push(...result.replacements ?? []);
                        rebuild(model);
                    }
                    committed = { schemaVersion: 1, kind: 'append', commitId: crypto.randomUUID(), scope: { ...model.scope },
                        records: structuredClone(batch.filter(record => !result.removedIds.includes(record.id))), removedIds: [...removed, ...result.removedIds] };
                    for (const key of result.evictedKeys) {
                        const other = models.get(key);
                        if (!other)
                            continue;
                        const queued = new Set([...other.queued.map((record) => record.id), ...other.savingIds ?? []]);
                        const snapshot = await journal.read(other.scope);
                        other.records = [...snapshot.records, ...other.records.filter(record => queued.has(record.id) && !snapshot.records.some(saved => saved.id === record.id))];
                        other.cuts = snapshot.cuts ?? [];
                        other.epochs = snapshot.epochs ?? other.epochs;
                        other.trimmed = true;
                        rebuild(other);
                        publish(other);
                    }
                }
                catch (error) {
                    model.writeError = { kind: 'write', cause: error };
                    model.queued.unshift(...batch);
                    for (const id of removed)
                        model.removed.add(id);
                    model.metadataDirty = true;
                    pause(model, model.writeError, !(error instanceof SessionCapacityError));
                }
                finally {
                    publish(model);
                }
            })();
            await model.saving;
            model.saving = undefined;
            model.savingIds = undefined;
            if (committed)
                notifyCommit(committed);
        };
        const schedule = () => {
            if (timer)
                return;
            timer = setTimeout(() => { timer = undefined; void get().flush(); }, options.flushDelayMs ?? 100);
        };
        const load = (scope) => {
            const key = sessionKey(scope);
            const existing = models.get(key);
            if (existing)
                return existing.load ?? Promise.resolve();
            const model = { observationGeneration: 0, authority: { status: scope.bindingId ? 'awaiting' : 'ready' }, scope: { ...scope }, records: [], cuts: [], epochs: {}, syncs: new Map(), candidates: new Map(), deferredFrames: [], deferredBytes: 0, queued: [], removed: new Set(), metadataDirty: false, projector: new SessionProjector(true), ordinal: 0, bytes: 0, loading: true, loaded: false, trimmed: false };
            models.set(key, model);
            publish(model);
            model.load = (async () => {
                try {
                    if (scope.bindingId)
                        await models.get(sessionKey({ ...scope, bindingId: undefined }))?.load;
                    const snapshot = await journal.read(scope);
                    if (!alive(model))
                        return;
                    const liveIds = new Set(model.records.map((record) => record.id));
                    const last = Math.max(0, ...snapshot.records.map(record => record.ordinal), ...(snapshot.cuts ?? []).flatMap(cut => [cut.ordinal, ...cut.records.map(record => record.ordinal)]));
                    model.records = model.records.map((record, index) => ({ ...record, ordinal: last + index + 1 }));
                    model.cuts = snapshot.cuts ?? [];
                    model.epochs = { ...snapshot.epochs, ...model.epochs };
                    model.records = [...snapshot.records.filter((record) => !liveIds.has(record.id)), ...model.records];
                    if (scope.bindingId) {
                        const legacy = models.get(sessionKey({ ...scope, bindingId: undefined }));
                        if (legacy) {
                            const migrated = new Set(snapshot.records.map(record => record.id));
                            const cuts = new Set(snapshot.cuts?.map(cut => cut.id));
                            legacy.records = legacy.records.filter(record => !migrated.has(record.id));
                            legacy.queued = legacy.queued.filter(record => !migrated.has(record.id));
                            legacy.cuts = legacy.cuts.filter(cut => !cuts.has(cut.id));
                            rebuild(legacy);
                            publish(legacy);
                        }
                    }
                    model.trimmed ||= snapshot.trimmed;
                    rebuild(model);
                    trimMemory(model);
                    model.checkpoint = model.projector.fork();
                }
                catch (error) {
                    if (!alive(model))
                        return;
                    pause(model, { kind: 'read', cause: error }, true);
                }
                finally {
                    model.loading = false;
                    model.loaded = true;
                    drainIngress(model);
                    publish(model);
                }
            })();
            return model.load;
        };
        const append = (model, record) => {
            if (frozenOf(model)?.projector && !recoveryOf(model))
                return;
            if (sessionRecordBytes(record) > journal.maxBytes) {
                pause(model, { kind: 'omission', cause: new SessionCapacityError() });
                return;
            }
            try {
                record = admit(model, record);
            }
            catch (cause) {
                pause(model, { kind: 'omission', cause });
                return;
            }
            if (record.presentation?.unavailable || record.presentation?.taskUnavailable) {
                pause(model, { kind: 'omission', cause: new SessionCapacityError() });
                return;
            }
            const bytes = sessionRecordBytes(record);
            model.records.push(record);
            model.queued.push(record);
            model.bytes += bytes;
            trimMemory(model);
            if (model.bytes > journal.maxBytes) {
                pause(model, { kind: 'omission', cause: new SessionCapacityError() });
                return;
            }
            publish(model);
            schedule();
            return !record.presentation?.unavailable && model.records.some(value => value.id === record.id) ? record : undefined;
        };
        const suspend = (matches) => {
            for (const model of models.values()) {
                if (!matches(model.scope))
                    continue;
                model.replacementAbort?.abort();
                discardIngress(model);
                const frozen = frozenOf(model);
                model.authority = frozen ? { status: 'paused', frozen } : { status: model.scope.bindingId ? 'awaiting' : model.authority.status === 'ready' ? 'ready' : 'awaiting' };
                model.observationGeneration++;
                model.availability = undefined;
                model.recoveryStatus = undefined;
                model.generation = undefined;
                model.interactionGeneration = undefined;
                model.lastSequence = undefined;
                sequences.delete(producerKey(model.scope) + '/timeline');
                sequences.delete(producerKey(model.scope) + '/state');
                publish(model);
            }
        };
        return {
            defaultSelectionRevisions: {},
            notifyDefaultSelection(scope) {
                if (revoked.has(channelKey(scope)))
                    return;
                const key = sessionKey({ ...scope, bindingId: undefined });
                set(state => ({ defaultSelectionRevisions: { ...state.defaultSelectionRevisions, [key]: (state.defaultSelectionRevisions[key] ?? 0) + 1 } }));
            },
            sessions: {},
            hydrate(scope) { revoked.delete(channelKey(scope)); return load(scope); },
            receiveAvailability(scope, frame) {
                if (revoked.has(channelKey(scope)) || !scope.bindingId || !validSessionAvailability(frame) || frame.channel_id !== scope.channelId || frame.agent_id !== scope.agentId || frame.session_control_binding_id !== scope.bindingId)
                    return false;
                void load(scope);
                const model = models.get(sessionKey(scope));
                const nativeSessionId = model.availability?.native_session_id ?? recoveryOf(model)?.nativeSessionId ?? model.projector.view(model.generation).runtimeInfo?.sessionId;
                if (nativeSessionId && nativeSessionId !== frame.native_session_id || model.availability && frame.revision <= model.availability.revision)
                    return false;
                model.availability = { ...frame };
                model.replacementAbort?.abort();
                discardIngress(model);
                model.authority = { status: 'paused', frozen: frozenOf(model) ?? { generation: model.generation, projector: model.checkpoint ?? new SessionProjector(true) } };
                publish(model);
                return frame.status === 'available';
            },
            receive(scope, frame, receivedAt = Date.now(), presentation) {
                const key = sessionKey(scope);
                if (revoked.has(channelKey(scope)) || frame.channel_id !== scope.channelId || frame.agent_id !== scope.agentId)
                    return;
                const normalized = normalizeActivityFrame(frame);
                if (!normalized)
                    return;
                frame = normalized;
                void load(scope);
                const model = models.get(key);
                if (scope.bindingId && frame.session_control_binding_id !== scope.bindingId)
                    return;
                if (frozenOf(model)?.projector && !recoveryOf(model))
                    return;
                if (recoveryOf(model)?.stage === 'preparing')
                    return;
                if (recoveryOf(model) && model.replacing && !model.drainingRecovery) {
                    enqueueIngress(model, { kind: 'frame', frame: structuredClone(frame), receivedAt, presentation, bytes: new TextEncoder().encode(JSON.stringify(frame)).byteLength });
                    return;
                }
                if (recoveryOf(model)) {
                    const recovery = recoveryOf(model);
                    const snapshot = frame.event.type === 'agent_snapshot' ? frame.event.payload : undefined;
                    const matches = snapshot && frame.timeline_epoch && frame.session_control_binding_id === scope.bindingId && snapshot.runtimeInfo.sessionId === recovery.nativeSessionId;
                    if (!recovery.snapshot && !matches)
                        return;
                }
                if (frame.event.type === 'timeline' && model.replacing && !model.drainingRecovery || model.loading && frame.timeline_epoch) {
                    const bytes = new TextEncoder().encode(JSON.stringify(frame)).byteLength;
                    enqueueIngress(model, { kind: 'frame', frame: structuredClone(frame), receivedAt, presentation, bytes });
                    return;
                }
                const sequenceKey = producerKey(scope) + (frame.event.type === 'timeline' ? '/timeline' : '/state');
                const lastSequence = sequences.get(sequenceKey);
                if (lastSequence !== undefined && frame.sequence <= lastSequence)
                    return;
                const runtime = frame.event.type === 'agent_snapshot' ? frame.event.payload.runtimeInfo : frame.event.type === 'runtime_updated' ? frame.event.runtimeInfo : undefined;
                const previousRuntime = model.projector.view(model.generation).runtimeInfo;
                if (runtime && previousRuntime && (runtime.providerId !== previousRuntime.providerId || runtime.sessionId !== previousRuntime.sessionId))
                    model.generation = undefined;
                if (!model.generation && runtime?.sessionId && frame.timeline_epoch) {
                    const saved = model.records.slice().reverse().find(record => {
                        const value = record.frame.event;
                        const info = value.type === 'runtime_updated' ? value.runtimeInfo : value.type === 'agent_snapshot' ? value.payload.runtimeInfo : undefined;
                        return info?.sessionId === runtime.sessionId && info.providerId === runtime.providerId;
                    });
                    model.generation = saved?.generation;
                }
                if (recoveryOf(model) && model.generation !== recoveryOf(model).generation) {
                    pause(model, { kind: 'read', cause: new Error('Session identity changed during recovery.') });
                    return;
                }
                model.generation ??= crypto.randomUUID();
                if (frame.timeline_epoch)
                    model.interactionGeneration ??= crypto.randomUUID();
                let previousTimelineEpoch;
                if (runtime && frame.timeline_epoch) {
                    const witness = frame.session_control_binding_id;
                    const latest = model.records.slice().reverse().find(record => record.generation === model.generation && ['runtime_updated', 'agent_snapshot'].includes(record.frame.event.type));
                    const previousWitness = latest?.frame.session_control_binding_id;
                    if (witness && previousWitness && witness !== previousWitness) {
                        for (const id of model.syncs.keys())
                            finishSync(model, id, false);
                        model.candidates.clear();
                        model.interactionGeneration = crypto.randomUUID();
                        model.timelineRecoveryStatus = undefined;
                        previousTimelineEpoch = model.epochs[model.generation];
                        model.epochs[model.generation] = frame.timeline_epoch;
                    }
                }
                if (runtime && frame.timeline_epoch && !model.epochs[model.generation])
                    model.epochs[model.generation] = frame.timeline_epoch;
                if (frame.event.type === 'timeline' && frame.timeline_epoch && model.epochs[model.generation] !== frame.timeline_epoch) {
                    const candidate = model.candidates.get(frame.timeline_epoch);
                    const bytes = new TextEncoder().encode(JSON.stringify(frame)).byteLength;
                    if (candidate && candidate.following.length < 4096 && [...model.candidates.values()].reduce((sum, value) => sum + value.bytes, bytes) <= 4 * 1024 * 1024) {
                        candidate.following.push({ frame: structuredClone(frame), receivedAt });
                        candidate.bytes += bytes;
                        model.timelineRecoveryStatus = 'unavailable';
                    }
                    else if (candidate)
                        failTimeline(model);
                    else
                        model.timelineRecoveryStatus = 'unavailable';
                    publish(model);
                    return;
                }
                sequences.set(sequenceKey, frame.sequence);
                model.lastSequence = frame.sequence;
                const record = append(model, { id: crypto.randomUUID(), generation: model.generation, interactionGeneration: model.interactionGeneration, previousTimelineEpoch, ordinal: ++model.ordinal, receivedAt, frame: structuredClone(frame), ...(presentation ? { presentation } : {}) });
                if (record && recoveryOf(model) && frame.event.type === 'agent_snapshot' && frame.event.payload.runtimeInfo.sessionId === recoveryOf(model).nativeSessionId)
                    recoveryOf(model).snapshot = true;
                const restored = [];
                if (frame.event.type === 'agent_snapshot' && !presentation?.unavailable) {
                    model.recoveryStatus = undefined;
                    publish(model);
                    for (const requestFrame of restoredInteractionFrames(frame, model.projector.view(model.generation).pending)) {
                        const accepted = append(model, { id: crypto.randomUUID(), generation: model.generation, interactionGeneration: model.interactionGeneration, ordinal: ++model.ordinal, receivedAt, frame: requestFrame });
                        if (accepted)
                            restored.push(accepted);
                    }
                }
                if (record && model.authority.status === 'ready')
                    notify('record', () => options.onRecord?.({ ...model.scope }, structuredClone(record), structuredClone(restored), structuredClone(model.projector.view(model.generation))), model.scope);
            },
            async replaceTimeline(scope, frame) {
                if (revoked.has(channelKey(scope)) || frame.channel_id !== scope.channelId || frame.agent_id !== scope.agentId || !validTimelineReplacement(frame.event, frame.protocol_version ?? PROTOCOL_VERSION) || frame.timeline_epoch !== frame.event.epoch)
                    return false;
                if (scope.bindingId && frame.session_control_binding_id !== scope.bindingId)
                    return false;
                void load(scope);
                const model = models.get(sessionKey(scope));
                if ((model.authority.status !== 'ready') && !recoveryOf(model))
                    return false;
                if (model.loading || model.replacing)
                    return new Promise(resolve => {
                        const bytes = new TextEncoder().encode(JSON.stringify(frame)).byteLength;
                        if (!enqueueIngress(model, { kind: 'replacement', frame: structuredClone(frame), resolve, bytes }))
                            resolve(false);
                    });
                const controller = new AbortController();
                model.replacementAbort = controller;
                model.replacing = (async () => {
                    await model.load;
                    await save(model);
                    if ((model.authority.status !== 'ready') && !recoveryOf(model))
                        return false;
                    const generation = model.generation;
                    const view = model.projector.view(generation);
                    if (!alive(model) || controller.signal.aborted || !generation || !journal.replaceTimeline || frame.session_control_binding_id !== view.sessionControlBindingId)
                        return false;
                    const matches = matchingSyncs(model, frame);
                    if (model.authority.status === 'ready' && model.epochs[generation] === frame.event.epoch && model.cuts.some(cut => cut.replacementId === frame.event.replacement_id && cut.epoch === frame.event.epoch && cut.digest === timelineReplacementDigest(frame.event))) {
                        for (const [id] of matches)
                            finishSync(model, id, true);
                        return true;
                    }
                    if (frame.sequence <= (sequences.get(producerKey(scope) + '/timeline') ?? -1)) {
                        for (const [id] of matches)
                            finishSync(model, id, false);
                        return false;
                    }
                    const previousEpoch = model.epochs[generation];
                    model.replacementAuthority = previousEpoch !== frame.event.previous_epoch && matches.length ? { generation, epoch: frame.event.epoch, replacementId: frame.event.replacement_id } : undefined;
                    if (((model.authority.status !== 'ready') || previousEpoch !== frame.event.previous_epoch) && !matches.length) {
                        const bytes = new TextEncoder().encode(JSON.stringify(frame)).byteLength;
                        const existing = model.candidates.get(frame.event.epoch);
                        if (existing && (existing.frame.sequence !== frame.sequence || timelineReplacementDigest(existing.frame.event) !== timelineReplacementDigest(frame.event))) {
                            failTimeline(model);
                            return false;
                        }
                        if ([...model.syncs.values()].some(sync => !sync.receipt) && !existing) {
                            if (model.candidates.size >= 4 || [...model.candidates.values()].reduce((sum, value) => sum + value.bytes, bytes) > 4 * 1024 * 1024) {
                                failTimeline(model);
                                return false;
                            }
                            model.candidates.set(frame.event.epoch, { frame: structuredClone(frame), following: [], bytes });
                        }
                        model.timelineRecoveryStatus = 'unavailable';
                        return false;
                    }
                    if ((model.authority.status !== 'ready') && (!recoveryOf(model)?.snapshot || recoveryOf(model).generation !== generation || recoveryOf(model).nativeSessionId !== view.runtimeInfo?.sessionId)) {
                        for (const [id] of matches)
                            finishSync(model, id, false);
                        return false;
                    }
                    let ordinal = model.ordinal + 1;
                    const records = frame.event.observations.map(row => {
                        if (!isPublicAgentTimelineEvent(row.event))
                            throw new Error('invalid_timeline_cut');
                        return { sourceKey: row.source_key, nativeRevision: row.native_revision, id: JSON.stringify([sessionKey(scope), generation, frame.event.replacement_id, row.source_key]), generation, ordinal: ++ordinal, receivedAt: Date.now(),
                            frame: { type: 'agent_activity', shape: 'normalized', protocol_version: frame.protocol_version ?? PROTOCOL_VERSION, channel_id: scope.channelId, agent_id: scope.agentId,
                                task_id: row.task_id ?? '', sequence: frame.sequence, occurred_at: row.occurred_at, timeline_epoch: frame.event.epoch, session_control_binding_id: frame.session_control_binding_id, event: row.event } };
                    });
                    const cut = { kind: 'timeline_replacement', digest: timelineReplacementDigest(frame.event), id: JSON.stringify([sessionKey(scope), frame.event.replacement_id]), generation, ordinal: model.ordinal + 1, receivedAt: Date.now(),
                        replacementId: frame.event.replacement_id, previousEpoch: previousEpoch ?? frame.event.previous_epoch, producerPreviousEpoch: frame.event.previous_epoch, epoch: frame.event.epoch, sequence: frame.sequence, records };
                    const candidate = model.projector.fork();
                    candidate.replaceTimeline(cut);
                    try {
                        await journal.replaceTimeline(scope, cut, { signal: controller.signal });
                        if (!alive(model) || controller.signal.aborted || model.generation !== generation)
                            return false;
                        model.records = model.records.filter(record => record.generation !== generation || record.frame.event.type !== 'timeline' || record.resolution);
                        model.queued = model.queued.filter(record => record.generation !== generation || record.frame.event.type !== 'timeline' || record.resolution);
                        model.cuts = [...model.cuts.filter(value => value.generation !== generation), cut];
                        model.epochs[generation] = cut.epoch;
                        model.ordinal = Math.max(model.ordinal, ordinal);
                        model.projector.replaceTimeline(cut);
                        sequences.set(producerKey(scope) + '/timeline', frame.sequence);
                        model.timelineRecoveryStatus = undefined;
                        model.writeError = undefined;
                        let recoveryRecords;
                        const recoveryContext = recoveryOf(model);
                        const recoveryReceipt = recoveryContext && model.syncs.get(recoveryContext.operationId)?.receipt;
                        if ((model.authority.status !== 'ready')) {
                            const recovery = recoveryOf(model);
                            const hasDeferredFrame = () => model.deferredFrames[0]?.kind === 'frame';
                            do {
                                model.drainingRecovery = true;
                                try {
                                    // A successor cut and its following frames retain their order until recovery commits.
                                    while (hasDeferredFrame()) {
                                        const item = model.deferredFrames.shift();
                                        model.deferredBytes -= item.bytes;
                                        if (item.kind === 'frame')
                                            get().receive(scope, item.frame, item.receivedAt, item.presentation);
                                    }
                                }
                                finally {
                                    model.drainingRecovery = false;
                                }
                                await save(model);
                                if (!recovery || recoveryOf(model) !== recovery || controller.signal.aborted || !alive(model))
                                    return false;
                            } while (hasDeferredFrame() || model.queued.length || model.metadataDirty);
                            recoveryRecords = model.records.filter(record => record.generation === generation && (record.interactionGeneration === model.interactionGeneration || record.resolution));
                            commitRecovery(model);
                        }
                        model.checkpoint = model.projector.fork();
                        model.bytes = model.records.reduce((bytes, record) => bytes + sessionRecordBytes(record), 0) + new TextEncoder().encode(JSON.stringify(cut)).byteLength;
                        notifyCommit({ schemaVersion: 1, commitId: crypto.randomUUID(), scope: { ...model.scope }, records: structuredClone(recoveryRecords ?? []),
                            ...(recoveryContext && recoveryReceipt ? { kind: 'recovery', operationId: recoveryContext.operationId, receipt: structuredClone(recoveryReceipt), cut: structuredClone(cut) } : { kind: 'timeline', cut: structuredClone(cut) }) });
                        notify('timelineReplacement', () => options.onTimelineReplacement?.({ ...model.scope }, structuredClone(frame), structuredClone(cut), structuredClone(model.projector.view(generation)), recoveryRecords && structuredClone(recoveryRecords)), model.scope);
                        for (const [id] of matchingSyncs(model, frame))
                            finishSync(model, id, true);
                        publish(model);
                        return true;
                    }
                    catch (error) {
                        if (!alive(model) || controller.signal.aborted || model.generation !== generation)
                            return false;
                        if (error instanceof Error && error.message === 'stale_timeline_epoch' && model.authority.status === 'ready') {
                            const saved = await journal.read(scope);
                            if (!alive(model) || controller.signal.aborted || model.generation !== generation)
                                return false;
                            const current = saved.cuts?.find(value => value.generation === generation);
                            if (current) {
                                model.projector.replaceTimeline(current);
                                model.records = model.records.filter(value => value.generation !== generation || value.frame.event.type !== 'timeline' || value.resolution);
                                model.cuts = [...model.cuts.filter(value => value.generation !== generation), current];
                                model.epochs = { ...model.epochs, ...saved.epochs };
                                model.ordinal = Math.max(model.ordinal, current.ordinal, ...current.records.map(value => value.ordinal));
                                model.bytes = model.records.reduce((bytes, value) => bytes + sessionRecordBytes(value), 0) + model.cuts.reduce((bytes, value) => bytes + new TextEncoder().encode(JSON.stringify(value)).byteLength, 0);
                                model.writeError = undefined;
                            }
                        }
                        else
                            pause(model, { kind: 'write', cause: error }, !(error instanceof SessionCapacityError) && !(error instanceof Error && error.message === 'stale_timeline_epoch'));
                        for (const [id] of matches)
                            finishSync(model, id, false);
                        model.timelineRecoveryStatus = 'failed';
                        return false;
                    }
                })();
                try {
                    return await model.replacing;
                }
                finally {
                    model.replacing = undefined;
                    model.replacementAbort = undefined;
                    model.replacementAuthority = undefined;
                    drainIngress(model);
                    publish(model);
                }
            },
            async syncTimeline(scope, operationId, invoke, timeoutMs = 31_000, expectedNativeSessionId) {
                const observationGeneration = models.get(sessionKey(scope))?.observationGeneration ?? 0;
                const availability = models.get(sessionKey(scope))?.availability;
                await load(scope);
                const model = models.get(sessionKey(scope));
                if (!model || model.observationGeneration !== observationGeneration || model.availability !== availability || availability && (availability.status !== 'available' || expectedNativeSessionId && expectedNativeSessionId !== availability.native_session_id) || !model.generation && model.authority.status === 'ready' || model.syncs.size >= 4 || revoked.has(channelKey(scope)))
                    return false;
                if ((model.authority.status !== 'ready')) {
                    if (recoveryOf(model) || !scope.bindingId)
                        return false;
                    const nativeSessionId = expectedNativeSessionId ?? (frozenOf(model)?.projector ?? model.projector).view(model.generation).runtimeInfo?.sessionId;
                    if (!nativeSessionId)
                        return false;
                    const frozen = { projector: frozenOf(model)?.projector ?? model.checkpoint ?? model.projector.fork(), generation: model.generation };
                    model.generation ??= model.records.slice().reverse().find(record => {
                        const event = record.frame.event;
                        const runtime = event.type === 'agent_snapshot' ? event.payload.runtimeInfo : event.type === 'runtime_updated' ? event.runtimeInfo : undefined;
                        return record.frame.session_control_binding_id === scope.bindingId && runtime?.sessionId === nativeSessionId;
                    })?.generation ?? crypto.randomUUID();
                    const recovery = { stage: 'preparing', operationId, generation: model.generation, nativeSessionId, snapshot: false };
                    model.authority = { status: 'recovering', frozen, recovery };
                    publish(model);
                    try {
                        await model.saving;
                        if (!alive(model) || recoveryOf(model) !== recovery || revoked.has(channelKey(scope)))
                            return false;
                        const saved = await journal.read(scope);
                        if (!alive(model) || recoveryOf(model) !== recovery || revoked.has(channelKey(scope)))
                            return false;
                        const confirmed = model.queued.filter(record => record.resolution && !saved.records.some(value => value.id === record.id));
                        model.records = [...saved.records, ...confirmed];
                        model.cuts = saved.cuts ?? [];
                        model.epochs = { ...saved.epochs };
                        model.queued = confirmed;
                        model.removed.clear();
                        model.metadataDirty = false;
                        rebuild(model);
                        if (recoveryOf(model) !== recovery)
                            return false;
                    }
                    catch (cause) {
                        pause(model, { kind: 'read', cause }, true);
                        return false;
                    }
                    model.interactionGeneration = crypto.randomUUID();
                    recovery.stage = 'observing';
                    model.timelineRecoveryStatus = undefined;
                    publish(model);
                }
                return new Promise(resolve => {
                    const timer = setTimeout(() => { finishSync(model, operationId, false); model.timelineRecoveryStatus = 'failed'; publish(model); }, timeoutMs);
                    const sync = { generation: model.generation, timer, resolve };
                    model.syncs.set(operationId, sync);
                    void invoke().then(async (receipt) => {
                        if (!alive(model) || model.syncs.get(operationId) !== sync || model.generation !== sync.generation || receipt.status !== 'confirmed' || receipt.operation_id !== operationId || !receipt.timeline_epoch || !receipt.replacement_id) {
                            finishSync(model, operationId, false);
                            return;
                        }
                        sync.receipt = receipt;
                        const candidate = model.candidates.get(receipt.timeline_epoch);
                        if (candidate?.frame.event.replacement_id === receipt.replacement_id) {
                            model.candidates.delete(receipt.timeline_epoch);
                            const recovering = (model.authority.status !== 'ready');
                            if (recovering)
                                for (const value of candidate.following) {
                                    if (!enqueueIngress(model, { kind: 'frame', frame: value.frame, receivedAt: value.receivedAt, bytes: new TextEncoder().encode(JSON.stringify(value.frame)).byteLength })) {
                                        finishSync(model, operationId, false);
                                        return;
                                    }
                                }
                            const accepted = await get().replaceTimeline(scope, candidate.frame);
                            if (accepted && !recovering)
                                for (const value of candidate.following)
                                    get().receive(scope, value.frame, value.receivedAt);
                            finishSync(model, operationId, accepted);
                        }
                        else if (model.authority.status === 'ready' && model.cuts.some(cut => cut.epoch === receipt.timeline_epoch && cut.replacementId === receipt.replacement_id))
                            finishSync(model, operationId, true);
                    }).catch(() => finishSync(model, operationId, false));
                });
            },
            needsTimelineSync(scope) { const model = models.get(sessionKey(scope)); return !!model && model.authority.status === 'ready' && !!model.timelineRecoveryStatus && !model.syncs.size; },
            reportTimelineRecovery(scope, epoch, status) {
                const model = models.get(sessionKey(scope));
                if (!model || !model.generation || model.epochs[model.generation] !== epoch)
                    return;
                model.timelineRecoveryStatus = status;
                publish(model);
            },
            reportRecovery(scope, status) {
                if (revoked.has(channelKey(scope)))
                    return;
                void load(scope);
                const model = models.get(sessionKey(scope));
                if (model.generation)
                    return;
                model.recoveryStatus = status;
                publish(model);
            },
            suspendAgent(accountId, guildId, agentId) { suspend((scope) => scope.accountId === accountId && (guildId === undefined || scope.guildId === guildId) && scope.agentId === agentId); },
            suspendAll(accountId, guildId) { suspend((scope) => scope.accountId === accountId && (guildId === undefined || scope.guildId === guildId)); },
            unloadChannel(accountId, guildId, channelId) {
                revoked.add(channelKey({ accountId, guildId, channelId }));
                const sessions = { ...get().sessions };
                for (const [key, model] of models) {
                    if (model.scope.accountId !== accountId || model.scope.guildId !== guildId || model.scope.channelId !== channelId)
                        continue;
                    model.replacementAbort?.abort();
                    discardIngress(model);
                    void save(model);
                    models.delete(key);
                    delete sessions[key];
                }
                const defaultSelectionRevisions = Object.fromEntries(Object.entries(get().defaultSelectionRevisions).filter(([key]) => {
                    const [account, guild, channel] = JSON.parse(key);
                    return account !== accountId || guild !== guildId || channel !== channelId;
                }));
                set({ sessions, defaultSelectionRevisions });
            },
            resetMemory(preserveDirectMessagesForAccount) {
                if (timer) {
                    clearTimeout(timer);
                    timer = undefined;
                }
                const sessions = {};
                const producers = new Set();
                for (const [key, model] of models) {
                    void save(model);
                    if (model.scope.accountId === preserveDirectMessagesForAccount && isDirectMessageSessionScope(model.scope)) {
                        sessions[key] = get().sessions[key];
                        producers.add(producerKey(model.scope) + '/timeline');
                        producers.add(producerKey(model.scope) + '/state');
                    }
                    else {
                        model.replacementAbort?.abort();
                        discardIngress(model);
                        models.delete(key);
                    }
                }
                for (const key of revoked) {
                    const [accountId, guildId] = JSON.parse(key);
                    if (accountId !== preserveDirectMessagesForAccount || !isDirectMessageSessionScope({ accountId, guildId }))
                        revoked.delete(key);
                }
                for (const key of sequences.keys())
                    if (!producers.has(key))
                        sequences.delete(key);
                const defaultSelectionRevisions = Object.fromEntries(Object.entries(get().defaultSelectionRevisions).filter(([key]) => {
                    const [accountId, guildId] = JSON.parse(key);
                    return accountId === preserveDirectMessagesForAccount && isDirectMessageSessionScope({ accountId, guildId });
                }));
                set({ sessions, defaultSelectionRevisions });
                for (const model of models.values())
                    publish(model);
            },
            applyResolution(scope, taskId, resolution, token) {
                const normalized = normalizeResolution(resolution);
                if (!normalized || normalized.payload.agentId !== scope.agentId)
                    return;
                resolution = normalized;
                const model = models.get(sessionKey(scope));
                if (!model || !model.generation)
                    return;
                const original = model.records.find(record => record.id === token && !record.resolution);
                if (!original || original.frame.task_id !== taskId || original.frame.event.type !== 'interaction_requested')
                    return;
                const request = original.frame.event.payload.request;
                if (request.requestId !== resolution.payload.requestId || request.kind !== resolution.payload.response.kind)
                    return;
                const frozen = frozenOf(model);
                const frozenView = frozen?.projector.view(frozen.generation);
                const current = original.generation === model.generation && original.interactionGeneration === model.interactionGeneration;
                const captured = original.generation === frozen?.generation && (frozenView?.pending.some(value => value.token === token) || frozenView?.entries.some(value => value.id === token));
                if (!current && !captured)
                    return;
                const confirmed = model.records.find(record => record.resolution && (record.requestToken === token || !record.requestToken
                    && record.generation === original.generation && record.interactionGeneration === original.interactionGeneration
                    && record.frame.task_id === taskId && record.frame.sequence === original.frame.sequence && record.resolution.payload.requestId === request.requestId));
                if (confirmed)
                    return structuredClone(confirmed);
                resolution = { ...resolution, payload: { ...resolution.payload, response: redactInteractionResponse(request, resolution.payload.response) } };
                const receipt = { ...original, id: crypto.randomUUID(), requestToken: token, ordinal: ++model.ordinal, receivedAt: Date.now(), resolution: { ...structuredClone(resolution), protocolVersion: normalized.protocolVersion } };
                if (frozenOf(model)?.projector) {
                    model.records.push(receipt);
                    model.queued.push(receipt);
                    model.bytes += sessionRecordBytes(receipt);
                    try {
                        frozenOf(model).projector.append(receipt);
                        if (recoveryOf(model)?.stage === 'observing')
                            model.projector.append(receipt);
                    }
                    catch (cause) {
                        pause(model, { kind: 'omission', cause });
                    }
                    publish(model);
                }
                else
                    append(model, receipt);
                return structuredClone(receipt);
            },
            async flush() {
                if (timer) {
                    clearTimeout(timer);
                    timer = undefined;
                }
                await Promise.all([...models.values()].map(save));
            },
        };
    })();
    state = initial;
    return { getState: get, getInitialState: () => initial, subscribe(listener) { listeners.add(listener); return () => { listeners.delete(listener); }; } };
}
//# sourceMappingURL=session-consumer.js.map